import firebase from 'firebase'
import { AuthService, RMUser, Credentials } from './api'
import { Subject, Observable } from 'rxjs'
import { map, switchMap, first, shareReplay } from 'rxjs/operators'

let instance: AuthService | null = null

export function authService(): AuthService {
  if (instance == null) {
    instance = new FirebaseAuthWebService()
  }
  return instance
}

class FirebaseAuthWebService implements AuthService {
  get loggedIn(): Promise<boolean> {
    return this.user.pipe(first(), map((u) => u != null && !u.isAnonymous)).toPromise()
  }

  get currentUser() {
    return firebase.auth().currentUser
  }

  public user: Observable<RMUser | null>;
  public waitListed = false;

  constructor() {
    const fbObserver = new Subject<RMUser | null>()
    this.user = fbObserver
      .pipe(switchMap((u) => this.addProfileInfo(u)))
      // make stream keep last value so new subscribers always get a value
      .pipe(shareReplay(1))
    // plug our observer/subject in firebase
    firebase.auth().onAuthStateChanged(fbObserver)
  }

  public register(user: Credentials): Promise<void> {
    return firebase.auth().createUserWithEmailAndPassword(user.email, user.password).then(() => undefined)
  }

  public async loginWithEmailPassword(user: Credentials): Promise<any> {
    const credential = await firebase.auth().signInWithEmailAndPassword(user.email, user.password)
    return credential
  }

  public async loginWithGoogle(): Promise<firebase.auth.UserCredential> {
    try {
      const provider = new firebase.auth.GoogleAuthProvider()
      await firebase.auth().signInWithRedirect(provider)
      return firebase.auth().getRedirectResult();
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  public resetPassword(email: string): Promise<void> {
    return firebase.auth().sendPasswordResetEmail(email)
  }

  public confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    return firebase.auth().confirmPasswordReset(code, newPassword)
  }

  public logout(): Promise<any> {
    return firebase.auth().signOut()
  }

  private async addProfileInfo(user: RMUser | null): Promise<RMUser | null> {
    if (user == null) {
      return null
    }
    const waitList = firebase.firestore().collection('waitList')

    try {
      const doc = await waitList.doc(user.uid).get()
      user.waitListed = doc.exists && (doc.data()!.isWaiting === true)
      return user
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  public biometricsAvailable(): Promise<boolean> {
    // @ts-ignore
    if (!("Fingerprint" in window)) return Promise.resolve(false)

    return new Promise(
      (resolve, reject) => {
        // @ts-ignore
        Fingerprint.isAvailable(resolve, (e) => {
          console.error(e)
          resolve(false)
        })
      })
  }

}
